import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "@/auth/SessionProvider";
import { LeadAccountFields, LeadContactFields, emptyChannelsState } from "@/components/crm/CrmLeadFormFields";
import { CrmOwnerSelect } from "@/components/crm/CrmOwnerSelect";
import { apiGet, apiPost } from "@/lib/apiClient";
import {
  channelOptionsFromLeadFields,
  defaultPhoneCountryIdFromLeadFields,
  type CrmChannelTypeOptions,
} from "@/lib/crmContactChannels";
import {
  addressOptionsFromLeadFields,
  defaultAddressCountryIdFromLeadFields,
  type CrmAddressTypeOptions,
} from "@/lib/crmContactAddresses";
import { CRM_LEAD_SOURCES, crmAccountIsConverted, formatCrmStatusLabel, normalizeCrmSourceForSave } from "@/lib/crmDisplay";
import { normalizeDomainFieldInput, validateLeadAccountWebFields } from "@/lib/crmDomainFromWebsite";
import {
  buildCreateContactApiBodyFromLeadContactForm,
  buildCreateLeadApiBody,
  LEAD_CONTACT_TO_CRM_CONTACT_NOTICE,
  leadContactCreateUsesCrmContact,
  type LeadAccountAttachMode,
  type LeadAttachedContactDraft,
  type LeadEntryKind,
} from "@/lib/crmLeadCreatePayload";
import {
  DEFAULT_CRM_LEAD_ENTRY,
  fetchCrmLeadModuleSettings,
  getCachedCrmLeadModuleDefaultEntry,
} from "@/lib/crmLeadModuleSettings";
import { fetchCrmLeadModuleFields, type CrmLeadModuleFieldsDocument } from "@/lib/crmLeadModuleFieldsApi";
import { sessionUserOwnerId, useCrmOrgUserDirectory } from "@/lib/crmOwnerUsers";
import {
  crmLeadFieldIsHidden,
  crmLeadSectionFieldById,
  crmLeadSelectOptionsForForm,
  crmLeadSelectOptionsForParentForm,
  crmLeadStatusFieldById,
  standardIndustrySegmentSelectOptions,
  standardIndustrySelectOptions,
} from "@/lib/crmLeadModuleFields";
import { ViewportNotification } from "@/components/ui/viewportNotification";
import { ArrowLeft, Trash2 } from "lucide-react";

type AccountOption = {
  id: string;
  name: string;
  domain?: string | null;
  status?: string | null;
};

const LEAD_STAGES = ["new", "qualified", "meeting_booked", "proposal_sent", "negotiation"] as const;
const LEAD_STATUSES = ["open", "working", "paused"] as const;

function fallbackLeadStatusOptions(
  values: readonly string[],
  labels?: Record<string, string>,
): { value: string; label: string }[] {
  return values.map((value) => ({
    value,
    label: labels?.[value] ?? formatCrmStatusLabel(value),
  }));
}

type AttachedLeadContactRow = LeadAttachedContactDraft & { id: string };

function createAttachedLeadContactRow(): AttachedLeadContactRow {
  return {
    id: crypto.randomUUID(),
    firstName: "",
    lastName: "",
    title: "",
    channels: emptyChannelsState("contact"),
  };
}

export function CrmLeadNewPage() {
  const navigate = useNavigate();
  const session = useSession();
  const sessionOwnerId = sessionUserOwnerId(session.data?.user);
  const { users: orgUsers, loading: orgUsersLoading } = useCrmOrgUserDirectory();
  const cachedDefaultEntry = getCachedCrmLeadModuleDefaultEntry();
  const [leadEntryReady, setLeadEntryReady] = useState(cachedDefaultEntry !== null);
  const [leadEntry, setLeadEntry] = useState<LeadEntryKind>(
    cachedDefaultEntry ?? DEFAULT_CRM_LEAD_ENTRY,
  );
  const [source, setSource] = useState("Manual");
  const [stage, setStage] = useState<string>("new");
  const [status, setStatus] = useState<string>("open");
  const [ownerId, setOwnerId] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [contactChannels, setContactChannels] = useState(() => emptyChannelsState("contact"));

  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [industrySegment, setIndustrySegment] = useState("");
  const [companyChannels, setCompanyChannels] = useState(() => emptyChannelsState("account"));

  const [attachMode, setAttachMode] = useState<LeadAccountAttachMode>("none");
  const [attachedContacts, setAttachedContacts] = useState<AttachedLeadContactRow[]>([]);
  const [existingAccountId, setExistingAccountId] = useState("");
  const [leadAccounts, setLeadAccounts] = useState<AccountOption[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    tone: "error" | "success";
    message: string;
  } | null>(null);

  const clearNotification = () => setNotification(null);
  const showError = (message: string) => setNotification({ tone: "error", message });
  const showSuccess = (message: string) => setNotification({ tone: "success", message });
  const [leadFieldSections, setLeadFieldSections] = useState<
    CrmLeadModuleFieldsDocument["sections"] | null
  >(null);

  const accountChannelOptions: CrmChannelTypeOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "account_lead", "account"),
    [leadFieldSections],
  );
  const contactChannelOptions: CrmChannelTypeOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead", "contact"),
    [leadFieldSections],
  );
  const accountDefaultPhoneCountryId = useMemo(
    () => defaultPhoneCountryIdFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactDefaultPhoneCountryId = useMemo(
    () => defaultPhoneCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const accountDefaultAddressCountryId = useMemo(
    () => defaultAddressCountryIdFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactDefaultAddressCountryId = useMemo(
    () => defaultAddressCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const accountAddressTypeOptions: CrmAddressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactAddressTypeOptions: CrmAddressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );

  const sourceField = useMemo(
    () => crmLeadStatusFieldById(leadFieldSections, "source"),
    [leadFieldSections],
  );
  const stageField = useMemo(
    () => crmLeadStatusFieldById(leadFieldSections, "stage"),
    [leadFieldSections],
  );
  const statusField = useMemo(
    () => crmLeadStatusFieldById(leadFieldSections, "status"),
    [leadFieldSections],
  );

  const sourceOptions = useMemo(() => {
    if (sourceField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(sourceField.selectOptions, source);
    }
    return CRM_LEAD_SOURCES.map((s) => ({ value: s, label: s }));
  }, [sourceField, source]);

  const stageOptions = useMemo(() => {
    if (stageField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(stageField.selectOptions, stage);
    }
    return fallbackLeadStatusOptions(LEAD_STAGES);
  }, [stageField, stage]);

  const statusOptions = useMemo(() => {
    if (statusField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(statusField.selectOptions, status);
    }
    return fallbackLeadStatusOptions(LEAD_STATUSES);
  }, [statusField, status]);

  const industryField = useMemo(
    () => crmLeadSectionFieldById(leadFieldSections, "account_lead", "industry"),
    [leadFieldSections],
  );
  const industrySegmentField = useMemo(
    () => crmLeadSectionFieldById(leadFieldSections, "account_lead", "industry_segment"),
    [leadFieldSections],
  );

  const industryOptions = useMemo(() => {
    if (industryField?.selectOptions?.length) {
      const fromApi = crmLeadSelectOptionsForForm(industryField.selectOptions, industry);
      if (fromApi.length) return fromApi;
    }
    return crmLeadSelectOptionsForForm(standardIndustrySelectOptions(), industry);
  }, [industryField, industry]);

  const industrySegmentOptions = useMemo(() => {
    const segmentSelectOptions = industrySegmentField?.selectOptions?.length
      ? industrySegmentField.selectOptions
      : standardIndustrySegmentSelectOptions();
    return crmLeadSelectOptionsForParentForm(
      segmentSelectOptions,
      industry,
      industrySegment,
    );
  }, [industrySegmentField, industry, industrySegment]);

  useEffect(() => {
    if (!industry) {
      if (industrySegment) setIndustrySegment("");
      return;
    }
    if (
      industrySegment &&
      industrySegmentOptions.length > 0 &&
      !industrySegmentOptions.some((o) => o.value === industrySegment)
    ) {
      setIndustrySegment("");
    }
  }, [industry, industrySegment, industrySegmentOptions]);

  useEffect(() => {
    if (!sessionOwnerId || ownerId) return;
    setOwnerId(sessionOwnerId);
  }, [sessionOwnerId, ownerId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [standard, fieldsResult] = await Promise.all([
        fetchCrmLeadModuleSettings(),
        fetchCrmLeadModuleFields(),
      ]);
      if (!cancelled) {
        setLeadEntry(standard);
        setLeadFieldSections(fieldsResult.sections);
        setLeadEntryReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{ accounts?: AccountOption[] }>("/api/crm/accounts?fetchAll=true");
      if (cancelled || !res.ok) return;
      setLeadAccounts(
        (res.data?.accounts ?? []).map((a) => ({
          id: a.id,
          name: a.name,
          domain: a.domain,
          status: a.status,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedAttachAccount = leadAccounts.find((a) => a.id === existingAccountId);
  const attachToConvertedAccount =
    leadEntry === "contact" &&
    attachMode === "existing" &&
    Boolean(selectedAttachAccount) &&
    crmAccountIsConverted(selectedAttachAccount?.status);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    clearNotification();
    const webFieldError = validateLeadAccountWebFields({
      website,
      domain: companyDomain,
    });
    if (webFieldError) {
      showError(webFieldError);
      return;
    }
    setIsSubmitting(true);
    try {
      const formSnapshot = {
        leadEntry,
        source: normalizeCrmSourceForSave(source),
        stage,
        status,
        ownerId: ownerId.trim() || sessionOwnerId,
        contact: {
          firstName,
          lastName,
          title,
          channels: contactChannels,
        },
        company: {
          name: companyName,
          domain: normalizeDomainFieldInput(companyDomain),
          website: website.trim(),
          industry,
          industrySegment,
          channels: companyChannels,
        },
        attach: {
          mode: leadEntry === "contact" ? attachMode : "none",
          existingAccountId,
          existingAccountName: selectedAttachAccount?.name || "",
          existingAccountDomain: selectedAttachAccount?.domain || "",
          existingAccountStatus: selectedAttachAccount?.status,
        },
        attachedContacts: attachedContacts.map(({ id: _id, ...draft }) => draft),
      };

      if (leadContactCreateUsesCrmContact(formSnapshot)) {
        const contactBody = buildCreateContactApiBodyFromLeadContactForm(formSnapshot);
        const res = await apiPost<{ contact?: { id?: string } }>("/api/crm/contacts", contactBody);
        const contactId = res.data?.contact?.id;
        if (!res.ok || !contactId) {
          throw new Error(res.error || "Failed to create contact.");
        }
        showSuccess("Contact created.");
        window.setTimeout(() => {
          void navigate(
            `/crm/contacts/${encodeURIComponent(contactId)}?accountId=${encodeURIComponent(existingAccountId)}`,
          );
        }, 700);
        return;
      }

      const body = buildCreateLeadApiBody(formSnapshot);
      const res = await apiPost<{ id?: string }>("/api/crm/leads", body);
      if (!res.ok || !res.data?.id) {
        throw new Error(res.error || "Failed to create lead.");
      }
      const leadId = res.data.id;
      showSuccess("Lead created.");
      window.setTimeout(() => {
        void navigate(`/crm/leads/${encodeURIComponent(leadId)}`);
      }, 700);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to create lead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const entryButtonClass = (kind: LeadEntryKind) =>
    kind === leadEntry
      ? "rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
      : "rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10";

  return (
    <div className="space-y-6">
      <ViewportNotification
        message={notification?.message ?? null}
        tone={notification?.tone ?? "error"}
        onDismiss={clearNotification}
        autoDismissMs={notification?.tone === "success" ? 5000 : undefined}
      />
      <Link
        to="/crm/leads"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All leads
      </Link>
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">New lead</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Capture a Lead Contact or Lead Account before conversion to CRM accounts and contacts.
        </p>
      </div>
      {!leadEntryReady ? (
        <div
          className="max-w-3xl rounded-lg border border-gray-200 p-8 text-sm text-slate-600 dark:border-white/10 dark:text-slate-400"
          aria-busy="true"
        >
          Loading lead form…
        </div>
      ) : null}
      {leadEntryReady ? (
      <form onSubmit={(e) => void handleSubmit(e)} className="max-w-3xl space-y-6">
        <fieldset className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-white/10">
          <legend className="px-1 text-sm font-semibold">Lead Entry</legend>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Choose whether you are creating a person lead or a company lead. This is not yet an Account or Contact
            record.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={entryButtonClass("account")}
              onClick={() => setLeadEntry("account")}
            >
              Lead Account
            </button>
            <button
              type="button"
              className={entryButtonClass("contact")}
              onClick={() => setLeadEntry("contact")}
            >
              Lead Contact
            </button>
          </div>
        </fieldset>

        <section className="rounded-lg border border-gray-200 p-4 dark:border-white/10">
          <h2 className="mb-3 text-sm font-semibold">Lead status</h2>
          <div className="space-y-3">
            <label className="block text-sm">
              Owner
              <CrmOwnerSelect
                value={ownerId}
                onChange={setOwnerId}
                users={orgUsers}
                loading={orgUsersLoading || session.status === "loading"}
                disabled={isSubmitting}
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {!sourceField || !crmLeadFieldIsHidden(sourceField) ? (
                <label className="text-sm">
                  {sourceField?.label ?? "Source"}
                  <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                  >
                    {sourceOptions.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {!stageField || !crmLeadFieldIsHidden(stageField) ? (
                <label className="text-sm">
                  {stageField?.label ?? "Stage"}
                  <select
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                  >
                    {stageOptions.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {!statusField || !crmLeadFieldIsHidden(statusField) ? (
                <label className="text-sm">
                  {statusField?.label ?? "Status"}
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                  >
                    {statusOptions.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </div>
        </section>

        {leadEntry === "contact" ? (
          <>
            <fieldset className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-white/10">
              <legend className="px-1 text-sm font-semibold">Lead Contact</legend>
              <LeadContactFields
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                title={title}
                setTitle={setTitle}
                channels={contactChannels}
                setChannels={setContactChannels}
                channelOptions={contactChannelOptions}
                defaultPhoneCountryId={contactDefaultPhoneCountryId}
                addressTypeOptions={contactAddressTypeOptions}
                defaultAddressCountryId={contactDefaultAddressCountryId}
              />
            </fieldset>

            <fieldset className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-white/10">
              <legend className="px-1 text-sm font-semibold">Attached Lead Account (optional)</legend>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Link an existing Lead Account or create one to associate with this Lead Contact. Converted CRM
                Accounts appear here too; selecting one creates a Contact instead of a Lead Contact.
              </p>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ["none", "None"],
                    ["existing", "Link existing"],
                    ["new", "Create new"],
                  ] as const
                ).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAttachMode(mode)}
                    className={
                      attachMode === mode
                        ? "rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white"
                        : "rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 dark:border-white/20 dark:text-gray-200"
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              {attachMode === "existing" ? (
                <select
                  required
                  value={existingAccountId}
                  onChange={(e) => setExistingAccountId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
                >
                  <option value="">Select account…</option>
                  {leadAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.domain ? ` (${a.domain})` : ""}
                      {crmAccountIsConverted(a.status) ? " · Account" : " · Lead Account"}
                    </option>
                  ))}
                </select>
              ) : null}
              {attachMode === "existing" && attachToConvertedAccount ? (
                <div
                  role="status"
                  className="rounded-md border border-indigo-200/80 bg-indigo-50 px-3 py-2 text-sm text-indigo-950 dark:border-indigo-400/30 dark:bg-indigo-950/40 dark:text-indigo-100"
                >
                  {LEAD_CONTACT_TO_CRM_CONTACT_NOTICE}
                </div>
              ) : null}
              {attachMode === "new" ? (
                <LeadAccountFields
                  companyName={companyName}
                  setCompanyName={setCompanyName}
                  companyDomain={companyDomain}
                  setCompanyDomain={setCompanyDomain}
                  website={website}
                  setWebsite={setWebsite}
                  industry={industry}
                  setIndustry={setIndustry}
                  industrySegment={industrySegment}
                  setIndustrySegment={setIndustrySegment}
                  industryOptions={industryOptions}
                  industrySegmentOptions={industrySegmentOptions}
                  channels={companyChannels}
                  setChannels={setCompanyChannels}
                  channelOptions={accountChannelOptions}
                  defaultPhoneCountryId={accountDefaultPhoneCountryId}
                  addressTypeOptions={accountAddressTypeOptions}
                  defaultAddressCountryId={accountDefaultAddressCountryId}
                  compact
                  leadFieldSections={leadFieldSections}
                />
              ) : null}
            </fieldset>
          </>
        ) : (
          <>
            <LeadAccountFields
              companyName={companyName}
              setCompanyName={setCompanyName}
              companyDomain={companyDomain}
              setCompanyDomain={setCompanyDomain}
              website={website}
              setWebsite={setWebsite}
              industry={industry}
              setIndustry={setIndustry}
              industrySegment={industrySegment}
              setIndustrySegment={setIndustrySegment}
              industryOptions={industryOptions}
              industrySegmentOptions={industrySegmentOptions}
              channels={companyChannels}
              setChannels={setCompanyChannels}
              channelOptions={accountChannelOptions}
              defaultPhoneCountryId={accountDefaultPhoneCountryId}
              addressTypeOptions={accountAddressTypeOptions}
              defaultAddressCountryId={accountDefaultAddressCountryId}
              leadFieldSections={leadFieldSections}
            />

            <fieldset className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-white/10">
              <legend className="px-1 text-sm font-semibold">New Lead Contact (optional)</legend>
              {attachedContacts.length > 0 ? (
                <div className="space-y-4">
                  {attachedContacts.map((row, index) => (
                    <div
                      key={row.id}
                      className="space-y-3 rounded-md border border-gray-200 p-4 dark:border-white/10"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          Lead Contact {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setAttachedContacts((prev) => prev.filter((item) => item.id !== row.id))
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          Remove
                        </button>
                      </div>
                      <LeadContactFields
                        firstName={row.firstName}
                        setFirstName={(value) =>
                          setAttachedContacts((prev) =>
                            prev.map((item) =>
                              item.id === row.id ? { ...item, firstName: value } : item,
                            ),
                          )
                        }
                        lastName={row.lastName}
                        setLastName={(value) =>
                          setAttachedContacts((prev) =>
                            prev.map((item) =>
                              item.id === row.id ? { ...item, lastName: value } : item,
                            ),
                          )
                        }
                        title={row.title}
                        setTitle={(value) =>
                          setAttachedContacts((prev) =>
                            prev.map((item) => (item.id === row.id ? { ...item, title: value } : item)),
                          )
                        }
                        channels={row.channels}
                        setChannels={(value) =>
                          setAttachedContacts((prev) =>
                            prev.map((item) => (item.id === row.id ? { ...item, channels: value } : item)),
                          )
                        }
                        channelOptions={contactChannelOptions}
                        defaultPhoneCountryId={contactDefaultPhoneCountryId}
                        addressTypeOptions={contactAddressTypeOptions}
                        defaultAddressCountryId={contactDefaultAddressCountryId}
                        embedded
                      />
                    </div>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => setAttachedContacts((prev) => [...prev, createAttachedLeadContactRow()])}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
              >
                Add Contact
              </button>
            </fieldset>
          </>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {isSubmitting
            ? "Creating…"
            : attachToConvertedAccount
              ? "Create contact"
              : "Create lead"}
        </button>
      </form>
      ) : null}
    </div>
  );
}
